<template>
  <div class="station-list-selection">
    <div class="station-list-selection__label">
      <label for="station-select">Wählen Sie einen Stützpunkt:</label>
    </div>
    <el-select
      id="station-select"
      v-model="selectedStationId"
      filterable
      placeholder="Name, PLZ oder Ort eingeben"
      :loading="loading"
      @change="onStationSelect"
      class="station-list-selection__select"
      :teleported="false"
      :fit-input-width="true"
    >
      <el-option
        v-for="station in sortedAllStations"
        :key="station.dienststelleId"
        :label="`${station.bezeichnung} (${station.plz} ${station.ort})`"
        :value="station.dienststelleId"
        :disabled="isStationDisabled(station)"
      >
        <div class="station-list-selection__item">
          <div class="station-list-selection__item-name">
            {{ station.bezeichnung }}
            <span v-if="station.distanceFromZipcodeInMeters">
              • {{ Math.round(station.distanceFromZipcodeInMeters / 1000) }} km
            </span>
            <span
              v-if="station.globalStpId === favoriteStation?.id"
              class="station-list-selection__item-favorite"
              >★</span
            >
          </div>
          <div class="station-list-selection__item-address">
            {{ station.adresse }}, {{ station.plz }} {{ station.ort }}
          </div>
          <div v-if="isStationDisabled(station)" class="station-list-selection__item-status">
            <template v-if="!inState(station)">Nicht im ausgewählten Bundesland</template>
            <template v-else-if="isTelephoneOnly(station)"
              >Nur telefonische Buchung möglich</template
            >
            <template v-else-if="isKeineTermine(station)">Keine Termine verfügbar</template>
            <template v-else-if="isUnbookable(station)">Nicht buchbar</template>
          </div>
        </div>
      </el-option>
    </el-select>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  initialStationId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['station-selected'])

const store = useStore()
const loading = ref(false)
const selectedStationId = ref(props.initialStationId)

// Computed properties from Vuex store
const allStations = computed(() => store.getters.allStations)
const favoriteStation = computed(() => store.getters.favoriteStation)
const selectedState = computed(() => store.getters.selectedState)
const telephoneStationIds = computed(() => store.getters.telephoneStationIds)
const keineTermineStationIds = computed(() => store.getters.keineTermineStationIds)
const unbookableStationIds = computed(() => store.getters.unbookableStationIds)
const sortedAllStations = computed(() => {
  return [...allStations.value].sort((a, b) => {
    const aInState = inState(a)
    const bInState = inState(b)

    if (aInState && !bInState) return -1 // a is in state, b is not -> a comes first
    if (!aInState && bInState) return 1 // b is in state, a is not -> b comes first

    // If both are in state or both are out of state, sort alphabetically by bezeichnung
    return a.bezeichnung.localeCompare(b.bezeichnung)
  })
})

// Watch for changes to initialStationId prop
watch(
  () => props.initialStationId,
  (newVal) => {
    selectedStationId.value = newVal
  }
)

// Functions to check station status
const inState = (station) => {
  return selectedState.value.id === station.bundeslandId
}

const isTelephoneOnly = (station) => {
  return telephoneStationIds.value.includes(station.dienststelleId)
}

const isKeineTermine = (station) => {
  return keineTermineStationIds.value.includes(station.dienststelleId)
}

const isUnbookable = (station) => {
  return unbookableStationIds.value.includes(station.dienststelleId)
}

const isStationDisabled = (station) => {
  return (
    !inState(station) ||
    isTelephoneOnly(station) ||
    isKeineTermine(station) ||
    isUnbookable(station)
  )
}

// Handle station selection
const onStationSelect = (stationId) => {
  const selectedStation = sortedAllStations.value.find(
    (station) => station.dienststelleId === stationId
  )

  if (selectedStation) {
    store.commit('setActiveStationId', stationId)
    emit('station-selected', selectedStation)
  }
}

// Fetch stations on component mount
const fetchStations = async () => {
  loading.value = true
  try {
    if (!allStations.value || allStations.value.length === 0) {
      // Fetch stations without parameters to get all stations
      await store.dispatch('getAllStations')
    }
  } catch (error) {
    console.error('Error fetching stations:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStations()
})
</script>

<style lang="scss">
.station-list-selection {
  width: 100%;

  &__label {
    margin-bottom: 8px;

    label {
      font-weight: $font-weight-medium;
    }
  }

  &__select {
    width: 100%;
    margin-bottom: 32px;
  }

  .el-select-dropdown__item {
    &.is-disabled {
      color: inherit;
      opacity: 0.7;
      cursor: not-allowed;
    }

    &.selected {
      background-color: rgba($color-info-primary, 0.1);
      font-weight: normal;
    }
  }

  &__item {
		padding-block: 8px;
		border-top: 1px solid $color-grey-light;

    &-name {
      font-weight: $font-weight-medium;
      margin-bottom: 4px;
    }

    &-favorite {
      color: $color-yellow-primary;
      margin-left: 4px;
    }

    &-address {
      font-size: 14px;
      color: $color-text-secondary;
    }

    &-status {
      margin-top: 8px;
      font-size: 14px;
      color: $color-danger-primary;
    }
  }
}
</style>
